/**
 * Roles Management Page
 * mode=system: SysAdmin mallar | mode=organization: kundroller
 */

import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useTranslation } from '@/store/languageStore';
import { ArrowLeftIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useRolesManagement, type RolesManagementMode } from '@/hooks/useRolesManagement';
import RolePermissionSelector from '@/components/roles/RolePermissionSelector';

interface RolesManagementProps {
  mode?: RolesManagementMode;
}

const RolesManagement = ({ mode = 'organization' }: RolesManagementProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isSystemMode = mode === 'system';
  const backRoute = isSystemMode ? ROUTES.SYSADMIN : ROUTES.SYSTEM_ADMINISTRATION;

  const {
    roles,
    roleTemplates,
    features,
    actions,
    loading,
    isGlobalAdmin,
    categorizedFeatures,
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    selectedRole,
    formData,
    setFormData,
    selectedTemplateId,
    handleTemplateChange,
    expandedCategories,
    selectedFeatureId,
    setSelectedFeatureId,
    selectedCategoryId,
    setSelectedCategoryId,
    showInactive,
    setShowInactive,
    toggleCategory,
    handleCreateRole,
    handleToggleRoleActive,
    handleTogglePermission,
    handleToggleAllActionsForFeature,
    openEditModal,
    handleUpdateRole,
    hasPermission,
    resetForm,
  } = useRolesManagement(mode);

  const normalizeId = (value: string | number | null | undefined): string =>
    value === null || value === undefined ? '' : String(value);

  if (loading) {
    return (
      <div className="p-4">
        <div className="text-textTertiary">{t('common.loading')}</div>
      </div>
    );
  }

  const sectionTitle = isSystemMode ? t('roles.systemRoles') : t('roles.customerRoles');
  const sectionHint = isSystemMode ? t('roles.systemTemplatesHint') : t('roles.visibleToOrganization');
  const sectionBadgeClass = isSystemMode
    ? 'px-2 py-1 bg-bgPrimary text-textLight text-xs rounded'
    : 'px-2 py-1 bg-blue-500 text-white text-xs rounded';

  const renderRoleCard = (role: typeof roles[0]) => (
    <div
      key={role.id}
      className={`p-4 bg-bgElevated rounded-lg border ${role.is_active ? 'border-border' : 'border-red-300 dark:border-red-800 opacity-60'}`}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-textDefault">{role.name}</h3>
            {!isSystemMode && role.organization_name && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded">
                {role.organization_name}
              </span>
            )}
          </div>
          <p className="text-sm text-textSecondary mt-1">{role.code}</p>
          {role.description && (
            <p className="text-sm text-textTertiary mt-2">{role.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(isSystemMode ? isGlobalAdmin : true) && (
            <button
              onClick={() => handleToggleRoleActive(role)}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                role.is_active
                  ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300'
                  : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300'
              }`}
            >
              {role.is_active ? t('common.deactivate') : t('common.activate')}
            </button>
          )}
          <button
            onClick={() => openEditModal(role)}
            className="px-3 py-1 text-sm rounded transition-colors bg-bgLight text-textDefault hover:bg-bgPrimary hover:text-textLight"
          >
            {t('common.edit')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(backRoute)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <ShieldCheckIcon className="w-6 h-6 text-primary" />
            {t('roles.title')}
          </h1>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity"
        >
          {t('roles.createRole')}
        </button>
      </div>

      <div className="mb-4 flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-textSecondary">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          {t('roles.showInactive')}
        </label>
      </div>

      {roles.length > 0 ? (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-textDefault mb-4 flex items-center gap-2">
            <span className={sectionBadgeClass}>{sectionTitle}</span>
            <span className="text-sm text-textTertiary font-normal">({sectionHint})</span>
          </h2>
          <div className="space-y-3">{roles.map(renderRoleCard)}</div>
        </div>
      ) : (
        <div className="text-center py-12 text-textTertiary">
          <p>{t('roles.noRolesFound')}</p>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bgContainer rounded-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-textDefault mb-4">{t('roles.createRole')}</h2>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">{t('roles.roleCode')} *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
                  placeholder={t('roles.codePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">{t('roles.roleName')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
                  placeholder={t('roles.namePlaceholder')}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-textDefault mb-1">{t('roles.roleDescription')}</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
                  rows={2}
                  placeholder={t('roles.descriptionPlaceholder')}
                />
              </div>

              {!isSystemMode && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('roles.templateStartingPoint')} *
                  </label>
                  <select
                    value={selectedTemplateId}
                    onChange={e => handleTemplateChange(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault"
                  >
                    <option value="">{t('roles.selectTemplate')}</option>
                    {roleTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.code})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-textTertiary mt-1">{t('roles.templateHint')}</p>
                </div>
              )}
            </div>

            <RolePermissionSelector
              categorizedFeatures={categorizedFeatures}
              features={features}
              actions={actions}
              formData={formData}
              setFormData={setFormData}
              expandedCategories={expandedCategories}
              toggleCategory={toggleCategory}
              selectedFeatureId={selectedFeatureId}
              setSelectedFeatureId={setSelectedFeatureId}
              selectedCategoryId={selectedCategoryId}
              setSelectedCategoryId={setSelectedCategoryId}
              handleToggleAllActionsForFeature={handleToggleAllActionsForFeature}
              handleTogglePermission={handleTogglePermission}
              hasPermission={hasPermission}
              isDisabled={false}
              t={t}
            />

            <div className="mt-6 p-4 bg-bgElevated rounded-lg">
              <h3 className="text-sm font-medium text-textDefault mb-2">
                {t('roles.summary')} ({formData.permissions.length} {t('roles.permissionsCount')})
              </h3>
              <div className="flex flex-wrap gap-2">
                {formData.permissions.length === 0 ? (
                  <p className="text-sm text-textTertiary">{t('roles.noPermissionsSelected')}</p>
                ) : (
                  features
                    .filter(f => formData.permissions.some(p => normalizeId(p.feature_id) === normalizeId(f.id)))
                    .map(feature => (
                      <span
                        key={feature.id}
                        className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded"
                      >
                        {feature.name}
                      </span>
                    ))
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="px-4 py-2 bg-bgLight text-textDefault rounded-lg hover:bg-bgElevated transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateRole}
                className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity"
              >
                {t('roles.createRoleButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedRole && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => { setShowEditModal(false); resetForm(); }}
        >
          <div
            className="bg-bgContainer rounded-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-textDefault mb-4">
              {t('roles.editRole')}
              {isSystemMode && ' (Systemmall)'}
            </h2>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">{t('roles.roleCode')}</label>
                <input type="text" value={formData.code} disabled className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault opacity-50" />
                <p className="text-xs text-textTertiary mt-1">{t('roles.codeCannotChange')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">{t('roles.roleName')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  disabled={isSystemMode && !isGlobalAdmin}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault disabled:opacity-50"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-textDefault mb-1">{t('roles.roleDescription')}</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  disabled={isSystemMode && !isGlobalAdmin}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault disabled:opacity-50"
                  rows={2}
                />
              </div>
            </div>

            <RolePermissionSelector
              categorizedFeatures={categorizedFeatures}
              features={features}
              actions={actions}
              formData={formData}
              setFormData={setFormData}
              expandedCategories={expandedCategories}
              toggleCategory={toggleCategory}
              selectedFeatureId={selectedFeatureId}
              setSelectedFeatureId={setSelectedFeatureId}
              selectedCategoryId={selectedCategoryId}
              setSelectedCategoryId={setSelectedCategoryId}
              handleToggleAllActionsForFeature={handleToggleAllActionsForFeature}
              handleTogglePermission={handleTogglePermission}
              hasPermission={hasPermission}
              isDisabled={isSystemMode && !isGlobalAdmin}
              t={t}
            />

            <div className="mt-6 p-4 bg-bgElevated rounded-lg">
              <h3 className="text-sm font-medium text-textDefault mb-2">
                {t('roles.summary')} ({formData.permissions.length} {t('roles.permissionsCount')})
              </h3>
              <div className="flex flex-wrap gap-2">
                {formData.permissions.length === 0 ? (
                  <p className="text-sm text-textTertiary">{t('roles.noPermissionsSelected')}</p>
                ) : (
                  features
                    .filter(f => formData.permissions.some(p => normalizeId(p.feature_id) === normalizeId(f.id)))
                    .map(feature => (
                      <span key={feature.id} className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded">
                        {feature.name}
                      </span>
                    ))
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => { setShowEditModal(false); resetForm(); }}
                className="px-4 py-2 bg-bgLight text-textDefault rounded-lg hover:bg-bgElevated transition-colors"
              >
                {t('common.cancel')}
              </button>
              {(!isSystemMode || isGlobalAdmin) && (
                <button
                  onClick={handleUpdateRole}
                  className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity"
                >
                  {t('common.saveChanges')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesManagement;
